/** Shared markdown renderer (lifted from the help system so AI-generated
 *  output reuses the same vetted path). Renders to React elements via
 *  react-markdown — no innerHTML, the `react/no-danger` rule stays intact.
 *  Internal links (`/help/...`, `/bookings`, …) stay in the SPA via the
 *  router; external links open in a new tab. */
import { Box, Link, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

function MdLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  if (href && href.startsWith('/')) {
    return (
      <Link component={RouterLink} to={href}>
        {children}
      </Link>
    );
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  );
}

export function Markdown({ source, maxWidth = 760 }: { source: string; maxWidth?: number | string }) {
  return (
    <Box
      sx={{
        maxWidth,
        '& > *:first-of-type': { mt: 0 },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.875em',
          bgcolor: 'action.hover',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.125,
        },
        '& pre': {
          bgcolor: 'action.hover',
          borderRadius: 1,
          p: 1.5,
          overflowX: 'auto',
          '& code': { bgcolor: 'transparent', p: 0 },
        },
        '& table': {
          display: 'block',
          overflowX: 'auto',
          borderCollapse: 'collapse',
          mb: 1.5,
        },
        '& th, & td': {
          border: 1,
          borderColor: 'divider',
          px: 1.25,
          py: 0.75,
          fontSize: 14,
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => <MdLink href={href}>{children}</MdLink>,
          p: ({ children }) => (
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              {children}
            </Typography>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="body1" sx={{ mb: 0.75 }}>
              {children}
            </Typography>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
              {children}
            </Box>
          ),
          h1: ({ children }) => (
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              {children}
            </Typography>
          ),
          blockquote: ({ children }) => (
            <Box
              component="blockquote"
              sx={{ borderLeft: 3, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' }}
            >
              {children}
            </Box>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}
